import { useIonToast, useIonViewWillEnter } from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DuplicateGroupItem, Track } from '../../types/Track';
import {
  fetchDuplicateTracks,
  loadIgnoredGroupNames,
  loadSession,
  saveGroupState,
} from './duplicateIpc';

export type DeleteProgress = {
  total: number;
  completed: number;
  failed: number;
  inProgress: boolean;
};

/** Stable id for a scan so resolved/ignored state survives a reload. */
const computeSessionId = (tracks: { id: number }[]): string => {
  const str = tracks
    .map((t) => t.id)
    .sort((a, b) => a - b)
    .join(',');
  let hash = 2166136261; // FNV offset basis (32-bit)
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 16777619) >>> 0;
  }
  return `scan_${hash.toString(16)}`;
};

const lowerArtist = (artist: unknown): string | undefined => {
  if (Array.isArray(artist)) return artist.join(',').toLowerCase();
  if (typeof artist === 'string') return artist.toLowerCase();
  return undefined;
};

const toGroupItem = (
  groupName: string,
  tracks: Track[],
  ignoredNames: string[],
): DuplicateGroupItem => {
  const firstTrack = tracks[0];
  return {
    id: groupName,
    name: groupName,
    trackCount: tracks.length,
    tracks: [],
    isResolved: false,
    isIgnored: ignoredNames.includes(groupName),
    likelyOriginalIndexCalculated: 0,
    title: firstTrack?.tags?.title?.toLowerCase() ?? undefined,
    artist: lowerArtist(firstTrack?.tags?.artist),
  };
};

/** Group names are "<title> by <artist>" when the group carries no keys. */
const getGroupMatchKeys = (group: DuplicateGroupItem) => {
  if (group.title !== undefined && group.artist !== undefined) {
    return { titleLc: group.title, artistLc: group.artist };
  }
  const match = group.name.match(/^(.+?)\s+by\s+(.+)$/s);
  return {
    titleLc: match?.[1]?.toLowerCase() ?? '',
    artistLc: match?.[2]?.toLowerCase() ?? '',
  };
};

const KEY_SEPARATOR = '\u0000';

/**
 * Bucket key for a track. Lets callers index the whole library once instead of
 * rescanning it for every group.
 */
export const trackBucketKey = (track: Track): string =>
  [
    String(track?.tags?.title || '').toLowerCase(),
    lowerArtist(track?.tags?.artist) ?? '',
  ].join(KEY_SEPARATOR);

/** The bucket key a group's tracks hash to. */
export const groupBucketKey = (group: DuplicateGroupItem): string => {
  const { titleLc, artistLc } = getGroupMatchKeys(group);
  return [titleLc, artistLc].join(KEY_SEPARATOR);
};

export type DuplicateResolverCore = ReturnType<typeof useDuplicateResolver>;

/**
 * Loads the duplicate scan and tracks how much of it has been handled. The
 * plan built on top of this (`useDuplicatePlan`) decides what to show.
 */
export const useDuplicateResolver = (embedded?: boolean) => {
  const location = useLocation<{
    duplicateGroups: Array<[string, Track[]]>;
    allTracks: Track[];
  }>();
  const [presentToast] = useIonToast();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionRestoredCount, setSessionRestoredCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [duplicateGroupItems, setDuplicateGroupItems] = useState<
    DuplicateGroupItem[]
  >([]);
  const [deleteProgress, setDeleteProgress] = useState<DeleteProgress>({
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: false,
  });

  const allTracksRef = useRef<Track[] | null>(null);
  const deleteInProgressRef = useRef(false);

  const persistGroupState = (
    groupId: string,
    isResolved: boolean,
    isIgnored: boolean,
  ) => {
    if (sessionId) saveGroupState(sessionId, groupId, isResolved, isIgnored);
  };

  const applyPersistedSession = async (
    groups: DuplicateGroupItem[],
    tracks: Track[],
  ): Promise<DuplicateGroupItem[]> => {
    if (tracks.length === 0) return groups;

    const sid = computeSessionId(tracks);
    setSessionId(sid);
    const session = await loadSession(sid);
    if (!session?.success || !session.groups) return groups;

    let restoredCount = 0;
    const merged = groups.map((group) => {
      const saved = session.groups[group.id];
      if (!saved) return group;
      // A group only counts as resolved if it really has nothing left to do.
      const isResolved =
        saved.isResolved && (group.trackCount ?? Infinity) <= 1;
      if (
        (isResolved && !group.isResolved) ||
        (saved.isIgnored && !group.isIgnored)
      ) {
        restoredCount++;
      }
      return { ...group, isResolved, isIgnored: saved.isIgnored };
    });

    if (restoredCount > 0) setSessionRestoredCount(restoredCount);
    return merged;
  };

  const loadDuplicateData = async () => {
    setIsLoading(true);
    const ignoredNames = await loadIgnoredGroupNames();

    try {
      // Navigating in from the library hands over an already-computed scan.
      if (location.state && Array.isArray(location.state.duplicateGroups)) {
        const allTracks = Array.isArray(location.state.allTracks)
          ? location.state.allTracks
          : [];
        allTracksRef.current = allTracks.length > 0 ? allTracks : null;

        setDuplicateGroupItems(
          await applyPersistedSession(
            location.state.duplicateGroups.map(([name, tracks]) =>
              toGroupItem(name, tracks || [], ignoredNames),
            ),
            allTracks,
          ),
        );
        return;
      }

      // Opened directly (or in a canvas): scan over IPC instead.
      const response = await fetchDuplicateTracks();
      if (
        !response ||
        !Array.isArray(response.tracks) ||
        !response.tracks.length
      ) {
        setDuplicateGroupItems([]);
        return;
      }

      allTracksRef.current = response.tracks;
      const { calculateDuplicateGroups } = await import(
        '../../utils/calculateDuplicateGroups'
      );
      setDuplicateGroupItems(
        await applyPersistedSession(
          calculateDuplicateGroups(response.tracks).map(
            ([name, tracks]: [string, Track[]]) =>
              toGroupItem(name, tracks, ignoredNames),
          ),
          response.tracks,
        ),
      );
    } catch (error) {
      console.warn('Failed to load duplicate groups:', error);
      setDuplicateGroupItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionRestoredCount > 0) {
      presentToast({
        message: `Picking up where you left off — ${sessionRestoredCount} set${
          sessionRestoredCount !== 1 ? 's' : ''
        } already handled.`,
        duration: 4000,
        color: 'primary',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionRestoredCount]);

  useIonViewWillEnter(() => {
    if (!embedded) loadDuplicateData();
  });

  useEffect(() => {
    // In a canvas the Ionic view lifecycle never fires, so mounting is the
    // only signal that the groups need loading.
    if (embedded) loadDuplicateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  useEffect(() => {
    const handleLibraryRefresh = () => loadDuplicateData();
    window.addEventListener(
      'library-refresh-notification',
      handleLibraryRefresh,
    );
    return () =>
      window.removeEventListener(
        'library-refresh-notification',
        handleLibraryRefresh,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    duplicateGroupItems,
    setDuplicateGroupItems,
    allTracksRef,
    isLoading,
    deleteProgress,
    setDeleteProgress,
    deleteInProgressRef,
    persistGroupState,
    loadDuplicateData,
  };
};
